import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

const FORCE_RESET_PATH = '/force-password-reset';

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, profile, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?next=${next}`} replace />;
  }

  // First-login password reset gate. Block every protected route until the
  // user clears the temporary password.
  if (profile?.requires_password_reset && location.pathname !== FORCE_RESET_PATH) {
    return <Navigate to={FORCE_RESET_PATH} replace />;
  }
  if (profile && !profile.requires_password_reset && location.pathname === FORCE_RESET_PATH) {
    return <Navigate to="/dashboard" replace />;
  }

  if (profile && profile.status && profile.status !== 'active') {
    const isSuspended = profile.status === 'suspended';
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center space-y-4 max-w-sm">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto ${isSuspended ? 'bg-orange-500/10' : 'bg-muted'}`}>
            <svg className={`w-8 h-8 ${isSuspended ? 'text-orange-500' : 'text-muted-foreground'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m2-6V7m0 0V5m0 2h2m-2 0H10" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-foreground">Acceso restringido</h2>
          <p className="text-muted-foreground text-sm">
            {isSuspended
              ? 'Tu cuenta ha sido suspendida. Contacta al administrador para más información.'
              : 'Tu cuenta está inactiva. Contacta al administrador para reactivarla.'}
          </p>
          <button
            onClick={() => supabase.auth.signOut()}
            className="text-sm text-primary underline underline-offset-4 hover:opacity-80"
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
